import {
  type StandingKind,
  StandingPanel,
  type StandingPanelProperties,
} from "./study-standing.js";

/**
 * Which save controls are corner badges, and the panel each badge opens.
 *
 * Apart from `study-standing.ts`, which is the panel. The press handlers for
 * words and 例句 ask here whether a control gets a panel at all.
 */

/**
 * Where the panel opens on its own, as a media query.
 *
 * The site's `large` breakpoint from `config/breakpoints`. The open panel is
 * 18rem wide and hangs from the card's top-right corner. Measured on a 词典
 * entry, it cleared the level line by 171px at 1501px and by a pixel or less
 * at 1101px to 1200px, where the sidebar takes the card's width.
 */
const roomToOpen = "(min-width: 1501px)";

/** The class the corner treatment wears, on the control. */
const badgeClass = "cb-save-word-badge";

/**
 * The panel for a badge, or nothing for any other control.
 * @param properties - The control and what the panel needs beside it.
 * @returns The panel, where the control is a corner badge.
 */
export function standingPanelFor(
  properties: Omit<StandingPanelProperties, "kind"> & {
    readonly kind?: StandingKind;
  },
): StandingPanel | undefined {
  if (!properties.control.classList.contains(badgeClass)) {
    return undefined;
  }

  return new StandingPanel({ ...properties, kind: properties.kind ?? "ci" });
}

/**
 * Whether a button is a corner badge's.
 *
 * The badge's held name says it opens something, where every other control's
 * says a press removes the subject.
 */
export function isBadge(button: HTMLButtonElement): boolean {
  return button.closest(`.${badgeClass}`) !== null;
}

/**
 * Open a badge's panel where the screen leaves room for it beside the entry.
 *
 * For a subject the page has just learned the reader holds. A reader arriving
 * at it sees the way to 复习 without pressing anything. Below `roomToOpen` the
 * panel would lie over the entry, so it waits for a press there. jsdom has no
 * `matchMedia`, and a window without it is taken to have no room.
 * @param panel - The badge's panel, where the control has one.
 * @param document - The page, whose window is asked about its width.
 */
export async function openWhereRoom(
  panel: StandingPanel | undefined,
  document: Document,
): Promise<void> {
  if (document.defaultView?.matchMedia?.(roomToOpen).matches === true) {
    await panel?.open();
  }
}
